import React, { useState, useEffect, useCallback } from "react";
import { Heart, MessageCircle, MapPin, Camera, Plus, X, Send, Image, Sparkles, Filter, ChevronDown, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTIVITY_TYPES = [
  { code: "playdate", label: "Playdate", emoji: "👫" },
  { code: "park", label: "Park Day", emoji: "🌳" },
  { code: "museum", label: "Museum Visit", emoji: "🏛️" },
  { code: "soft_play", label: "Soft Play", emoji: "🎪" },
  { code: "craft", label: "Arts & Crafts", emoji: "🎨" },
  { code: "party", label: "Birthday Party", emoji: "🎂" },
  { code: "swimming", label: "Swimming", emoji: "🏊" },
  { code: "nature", label: "Nature Walk", emoji: "🍂" },
  { code: "sports", label: "Sports Day", emoji: "⚽" },
  { code: "cooking", label: "Cooking/Baking", emoji: "🧁" },
  { code: "library", label: "Library Visit", emoji: "📚" },
  { code: "zoo", label: "Zoo/Farm", emoji: "🦁" },
  { code: "beach", label: "Beach Day", emoji: "🏖️" },
  { code: "other", label: "Other Fun", emoji: "✨" },
];

const Community = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterType, setFilterType] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  
  // Create post form state
  const [newCaption, setNewCaption] = useState("");
  const [newActivityType, setNewActivityType] = useState("");
  const [newLocation, setNewLocation] = useState("");
  const [newPhotoUrls, setNewPhotoUrls] = useState([]);
  const [newPhotoInput, setNewPhotoInput] = useState("");
  const [creating, setCreating] = useState(false);

  // Comment state
  const [commentingOn, setCommentingOn] = useState(null);
  const [commentText, setCommentText] = useState("");
  
  // Weekly highlights state
  const [highlights, setHighlights] = useState([]);
  const [highlightsLoading, setHighlightsLoading] = useState(true);

  const fetchHighlights = useCallback(async () => {
    setHighlightsLoading(true);
    try {
      const response = await axios.get(`${API}/community/weekly-highlights`);
      setHighlights(response.data.highlights || []);
    } catch (error) {
      console.error("Failed to load highlights");
    } finally {
      setHighlightsLoading(false);
    }
  }, []);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append("activity_type", filterType);
      
      const response = await axios.get(`${API}/community/posts?${params.toString()}`);
      setPosts(response.data.posts || []);
    } catch (error) {
      toast.error("Failed to load community posts");
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    fetchPosts();
    fetchHighlights();
  }, [fetchPosts, fetchHighlights]);

  const handleCreatePost = async () => {
    if (!newCaption.trim()) {
      toast.error("Please add a caption for your post");
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API}/community/posts`, {
        caption: newCaption,
        photo_urls: newPhotoUrls,
        activity_type: newActivityType,
        location: newLocation,
      });
      
      toast.success("Post shared with the community!");
      setShowCreateModal(false);
      setNewCaption("");
      setNewActivityType("");
      setNewLocation("");
      setNewPhotoUrls([]);
      fetchPosts();
    } catch (error) {
      toast.error("Failed to create post");
    } finally {
      setCreating(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const response = await axios.post(`${API}/community/posts/${postId}/like`);
      setPosts(posts.map(p => 
        p.post_id === postId 
          ? { ...p, is_liked: response.data.liked, like_count: response.data.like_count }
          : p
      ));
    } catch (error) {
      toast.error("Failed to like post");
    }
  };

  const handleComment = async (postId) => {
    if (!commentText.trim()) return;
    
    try {
      const response = await axios.post(`${API}/community/posts/${postId}/comment`, {
        comment: commentText
      });
      
      setPosts(posts.map(p => 
        p.post_id === postId 
          ? { 
              ...p, 
              comments: [...(p.comments || []), response.data.comment],
              comment_count: (p.comment_count || 0) + 1
            }
          : p
      ));
      setCommentText("");
      setCommentingOn(null);
      toast.success("Comment added!");
    } catch (error) {
      toast.error("Failed to add comment");
    }
  };

  const addPhotoUrl = () => {
    if (newPhotoInput.trim() && newPhotoUrls.length < 2) {
      setNewPhotoUrls([...newPhotoUrls, newPhotoInput.trim()]);
      setNewPhotoInput("");
    }
  };

  const removePhotoUrl = (index) => {
    setNewPhotoUrls(newPhotoUrls.filter((_, i) => i !== index));
  };

  const getActivityEmoji = (code) => {
    const activity = ACTIVITY_TYPES.find(a => a.code === code);
    return activity ? activity.emoji : "✨";
  };

  const getActivityLabel = (code) => {
    const activity = ACTIVITY_TYPES.find(a => a.code === code);
    return activity ? activity.label : code;
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-[#FFF9F5] pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-slate-200 px-6 py-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Community
              </h1>
              <p className="text-sm text-slate-500">See what families are doing together</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="rounded-full"
                data-testid="filter-btn"
              >
                <Filter className="w-4 h-4 mr-1" />
                Filter
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </Button>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-full"
                data-testid="create-post-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Share
              </Button>
            </div>
          </div>
          
          {/* Filter dropdown */}
          {showFilters && (
            <div className="mt-4 p-4 bg-slate-50 rounded-xl animate-fade-in">
              <label className="text-sm font-medium text-slate-700 block mb-2">Activity Type</label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilterType("")}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    filterType === "" 
                      ? "bg-purple-500 text-white" 
                      : "bg-white text-slate-600 border border-slate-200"
                  }`}
                >
                  All
                </button>
                {ACTIVITY_TYPES.map((type) => (
                  <button
                    key={type.code}
                    onClick={() => setFilterType(type.code)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      filterType === type.code 
                        ? "bg-purple-500 text-white" 
                        : "bg-white text-slate-600 border border-slate-200"
                    }`}
                  >
                    {type.emoji} {type.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Weekly Highlights Section */}
      {highlights.length > 0 && !filterType && (
        <section className="max-w-4xl mx-auto px-4 py-6" data-testid="weekly-highlights">
          <div className="mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center">
              <Star className="w-4 h-4 text-white" fill="white" />
            </div>
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Weekly Highlights
            </h2>
            <span className="text-sm text-slate-500">• Most loved this week</span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {highlights.slice(0, 6).map((post) => (
              <div 
                key={post.post_id} 
                className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-all cursor-pointer group"
                data-testid={`highlight-${post.post_id}`}
              >
                {/* Photo or gradient placeholder */}
                <div className="aspect-square relative overflow-hidden">
                  {post.photo_urls?.length > 0 ? (
                    <img 
                      src={post.photo_urls[0]} 
                      alt="Activity" 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  ) : (
                    <div className={`w-full h-full bg-gradient-to-br from-purple-400 via-pink-400 to-orange-300 flex items-center justify-center`}>
                      <span className="text-4xl">{getActivityEmoji(post.activity_type)}</span>
                    </div>
                  )}
                  {/* Like count badge */}
                  <div className="absolute bottom-2 right-2 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1 text-xs font-medium text-pink-600">
                    <Heart className="w-3 h-3" fill="#EC4899" />
                    {post.like_count || 0}
                  </div>
                  {/* Activity type badge */}
                  {post.activity_type && (
                    <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 text-xs font-medium">
                      {getActivityEmoji(post.activity_type)}
                    </div>
                  )}
                </div>
                {/* Caption preview */}
                <div className="p-3">
                  <p className="text-sm font-medium text-slate-800 line-clamp-2">{post.caption}</p>
                  <p className="text-xs text-slate-500 mt-1">{post.parent_name}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Posts Feed */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 rounded-full bg-gradient-to-r from-pink-100 to-purple-100 flex items-center justify-center mx-auto mb-6">
              <Camera className="w-12 h-12 text-purple-400" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              No posts yet
            </h2>
            <p className="text-slate-500 mb-6">Be the first to share what your family has been up to!</p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Share Your First Post
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map((post) => (
              <div key={post.post_id} className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="community-post">
                {/* Post Header */}
                <div className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-400 to-purple-400 flex items-center justify-center text-white font-bold">
                    {post.parent_name?.[0] || "?"}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800">{post.parent_name}</p>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      {post.location_city && (
                        <>
                          <MapPin className="w-3 h-3" />
                          <span>{post.location_city}</span>
                          <span>•</span>
                        </>
                      )}
                      <span>{formatTimeAgo(post.created_at)}</span>
                    </div>
                  </div>
                  {post.activity_type && (
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                      {getActivityEmoji(post.activity_type)} {getActivityLabel(post.activity_type)}
                    </span>
                  )}
                </div>

                {/* Post Photos */}
                {post.photo_urls?.length > 0 && (
                  <div className={`grid gap-1 ${post.photo_urls.length === 1 ? '' : 'grid-cols-2'}`}>
                    {post.photo_urls.slice(0, 4).map((url, i) => (
                      <div key={i} className={`relative ${post.photo_urls.length === 1 ? 'aspect-video' : 'aspect-square'}`}>
                        <img
                          src={url}
                          alt={`Activity photo ${i + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                        {i === 3 && post.photo_urls.length > 4 && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-2xl font-bold">
                            +{post.photo_urls.length - 4}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Caption */}
                <div className="p-4">
                  <p className="text-slate-700 whitespace-pre-wrap">{post.caption}</p>
                </div>

                {/* Actions */}
                <div className="px-4 pb-4 flex items-center gap-6">
                  <button
                    onClick={() => handleLike(post.post_id)}
                    className={`flex items-center gap-2 transition-colors ${
                      post.is_liked ? "text-pink-500" : "text-slate-500 hover:text-pink-500"
                    }`}
                    data-testid={`like-btn-${post.post_id}`}
                  >
                    <Heart className="w-5 h-5" fill={post.is_liked ? "#EC4899" : "none"} />
                    <span className="font-medium">{post.like_count || 0}</span>
                  </button>
                  <button
                    onClick={() => setCommentingOn(commentingOn === post.post_id ? null : post.post_id)}
                    className="flex items-center gap-2 text-slate-500 hover:text-purple-500 transition-colors"
                    data-testid={`comment-btn-${post.post_id}`}
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span className="font-medium">{post.comment_count || 0}</span>
                  </button>
                </div>

                {/* Comments Section */}
                {(post.comments?.length > 0 || commentingOn === post.post_id) && (
                  <div className="px-4 pb-4 border-t border-slate-100 pt-3">
                    {/* Existing comments */}
                    {post.comments?.slice(-3).map((comment) => (
                      <div key={comment.comment_id} className="flex gap-2 mb-2">
                        <span className="font-semibold text-sm text-slate-800">{comment.parent_name}</span>
                        <span className="text-sm text-slate-600">{comment.comment}</span>
                      </div>
                    ))}
                    {post.comments?.length > 3 && (
                      <button className="text-sm text-purple-500 mb-2">
                        View all {post.comment_count} comments
                      </button>
                    )}
                    
                    {/* Add comment input */}
                    {commentingOn === post.post_id && (
                      <div className="flex gap-2 mt-2">
                        <Input
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          placeholder="Add a comment..."
                          className="flex-1 h-10 rounded-full"
                          onKeyPress={(e) => e.key === "Enter" && handleComment(post.post_id)}
                          data-testid="comment-input"
                        />
                        <Button
                          onClick={() => handleComment(post.post_id)}
                          size="sm"
                          className="rounded-full bg-purple-500 hover:bg-purple-600"
                          data-testid="send-comment-btn"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Post Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Share with the Community
              </h2>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Activity Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">What did you do?</label>
                <div className="flex flex-wrap gap-2">
                  {ACTIVITY_TYPES.map((type) => (
                    <button
                      key={type.code}
                      type="button"
                      onClick={() => setNewActivityType(type.code)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        newActivityType === type.code
                          ? "bg-purple-500 text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {type.emoji} {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Caption */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tell us about it</label>
                <Textarea
                  value={newCaption}
                  onChange={(e) => setNewCaption(e.target.value)}
                  placeholder="Had an amazing playdate at the park! The kids loved the new climbing frame..."
                  className="min-h-[100px] rounded-xl"
                  maxLength={500}
                  data-testid="caption-input"
                />
                <p className="text-xs text-slate-400 mt-1">{newCaption.length}/500</p>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Where? (optional)</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={newLocation}
                    onChange={(e) => setNewLocation(e.target.value)}
                    placeholder="e.g., Heaton Park, Manchester"
                    className="pl-10 rounded-xl"
                    data-testid="location-input"
                  />
                </div>
              </div>

              {/* Photo URLs */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Image className="w-4 h-4 inline mr-1" />
                  Add Photos (up to 2)
                </label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newPhotoInput}
                    onChange={(e) => setNewPhotoInput(e.target.value)}
                    placeholder="Paste image URL..."
                    className="flex-1 rounded-xl"
                    data-testid="photo-url-input"
                  />
                  <Button
                    type="button"
                    onClick={addPhotoUrl}
                    disabled={newPhotoUrls.length >= 2}
                    variant="outline"
                    className="rounded-xl"
                  >
                    Add
                  </Button>
                </div>
                {newPhotoUrls.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {newPhotoUrls.map((url, i) => (
                      <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden">
                        <img src={url} alt={`Preview ${i + 1}`} className="w-full h-full object-cover" />
                        <button
                          onClick={() => removePhotoUrl(i)}
                          className="absolute top-1 right-1 w-5 h-5 bg-black/50 rounded-full flex items-center justify-center"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-400 mt-2">
                  Tip: Upload photos to Imgur or similar and paste the direct image URL
                </p>
              </div>

              {/* Submit */}
              <Button
                onClick={handleCreatePost}
                disabled={creating || !newCaption.trim()}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-xl py-6"
                data-testid="submit-post-btn"
              >
                {creating ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Sharing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Share with Community
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default Community;
